import { IStyle, ITextFieldProps, Label, MessageBarType } from '@fluentui/react';
import {
  Button,
  CodeBlock,
  IH2OTheme,
  Info,
  KeyValuePairEditor,
  Loader,
  TextField,
  buttonStylesPrimary,
  buttonStylesStealth,
  loaderStylesSpinnerButtonPrimary,
  useClassNames,
  useTheme,
  useToast,
} from '@h2oai/ui-kit';
import React from 'react';

import { ClassNamesFromIStyles } from '../../utils/models';
import { formatError } from '../../utils/utils';
import { useConfirmDialog } from './ConfirmDialogProvider';

export interface IDeploymentTabScoringStyles {
  form: IStyle;
  loader: IStyle;
  customEndpointButton: IStyle;
  infoLabel: IStyle;
  tooltip: IStyle;
  loaderWrapper: IStyle;
}

export const deploymentTabScoringStyles = (theme: IH2OTheme): Partial<IDeploymentTabScoringStyles> => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: 20,
    margin: '0px 40px',
    // TODO: Remove once theme is used.
    borderColor: theme.semanticColors?.bodyBackground,
  },
  loader: {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  customEndpointButton: {
    margin: '6px 0',
  },
  infoLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  tooltip: {
    padding: 4,
  },
  loaderWrapper: { height: 360, display: 'flex', justifyContent: 'center', alignItems: 'center' },
});

export interface IDeploymentTabScoringProps {
  defaultSecurityLevel?: string;
  scorerEndpoint?: string;
  sampleRequestUrl?: string;
  defaultScoringPassphrase?: string;
  isNew: boolean;
}

const scoringConfigToJSON = (scoringConfig: { [key: string]: string }) =>
    JSON.stringify({
      fields: Object.keys(scoringConfig),
      rows: [Object.values(scoringConfig)],
    }),
  sampleDataToScoringConfig = (sampleData: { fields: string[]; rows: string[] }) => {
    if (
      !sampleData ||
      !sampleData.fields ||
      !sampleData.rows ||
      sampleData.fields.length === 0 ||
      sampleData.rows.length === 0
    ) {
      return {};
    }
    return sampleData.fields.reduce((acc: { [key: string]: string }, field: string, index: number) => {
      acc[field] = sampleData.rows[0][index];
      return acc;
    }, {});
  };

export const DeploymentTabScoring = ({
  defaultSecurityLevel,
  scorerEndpoint,
  sampleRequestUrl,
  defaultScoringPassphrase,
  isNew,
}: IDeploymentTabScoringProps) => {
  const theme = useTheme(),
    classNames = useClassNames<IDeploymentTabScoringStyles, ClassNamesFromIStyles<IDeploymentTabScoringStyles>>(
      'deploymentTabScoringStyles',
      deploymentTabScoringStyles(theme)
    ),
    { addToast } = useToast(),
    { showDialog } = useConfirmDialog(),
    isSecured = React.useMemo(() => defaultSecurityLevel !== 'no_security', [defaultSecurityLevel]),
    // TODO: Handle loading.
    [isScoring, setIsScoring] = React.useState<boolean>(false),
    [sampleScoringConfig, setSampleScoringConfig] = React.useState<{ [key: string]: string }>(),
    [isSamplePopulated, setIsSamplePopulated] = React.useState<boolean>(false),
    [isPassphraseSet, setIsPassphraseSet] = React.useState<boolean>(false),
    [showPassphraseValidation, setShowPassphraseValidation] = React.useState<boolean>(false),
    [validatingPassphrase, setValidatingPassphrase] = React.useState<boolean>(false),
    [scoringConfig, setScoringConfig] = React.useState<{ [key: string]: string }>({ Field: 'column' }),
    [isDefaultConfigChanged, setIsDefaultConfigChanged] = React.useState<boolean>(false),
    [scoringResponse, setScoringResponse] = React.useState<string>(),
    [scoringPassphrase, setScoringPassphrase] = React.useState<string>(defaultScoringPassphrase || ''),
    score = React.useCallback(async () => {
      setIsScoring(true);
      try {
        const body = scoringConfigToJSON(scoringConfig),
          headers = new Headers();
        headers.append('Content-Type', 'application/json');
        if (defaultSecurityLevel !== 'no_security') {
          headers.append('Authorization', `Bearer ${scoringPassphrase}`);
        }
        const response = await fetch((scorerEndpoint as string) || '', {
          method: 'POST',
          headers,
          body,
        });

        setScoringResponse(await response.text());

        addToast({
          messageBarType: MessageBarType.success,
          message: 'Scoring successfull.',
        });
      } catch (err) {
        const message = `Failed to score: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
      } finally {
        setIsScoring(false);
      }
    }, [addToast, scoringConfig, scorerEndpoint, defaultSecurityLevel, scoringPassphrase]),
    getSampleRequest = React.useCallback(
      async (url: string) => {
        try {
          setValidatingPassphrase(true);
          const headers = new Headers();
          headers.append('Content-Type', 'application/json');
          if (defaultSecurityLevel !== 'no_security') {
            headers.append('Authorization', `Bearer ${scoringPassphrase}`);
          }
          const response = await fetch(url || '', {
            method: 'GET',
            headers,
          });

          if (response?.status === 401) {
            setShowPassphraseValidation(true);
            return;
          }

          setSampleScoringConfig(sampleDataToScoringConfig(await response.json()));
          setIsPassphraseSet(true);
          setShowPassphraseValidation(false);
        } catch (err) {
          const message = `Failed to get sample request: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          setShowPassphraseValidation(false);
        } finally {
          setValidatingPassphrase(false);
        }
      },
      [defaultSecurityLevel, scoringPassphrase]
    ),
    keyValuePairEditorStyles = React.useMemo(
      () => ({
        root: {
          backgroundColor: theme.semanticColors?.contentBackground,
          maxWidth: 460,
          border: `1px solid ${theme.semanticColors?.inputBorder}`,
          borderRadius: 4,
        },
      }),
      [theme.semanticColors?.contentBackground, theme.semanticColors?.inputBorder]
    );

  React.useEffect(() => {
    if (!scoringPassphrase && defaultScoringPassphrase) setScoringPassphrase(defaultScoringPassphrase);
  }, [defaultScoringPassphrase, scoringPassphrase]);

  React.useEffect(() => {
    if (sampleRequestUrl && (defaultSecurityLevel === 'no_security' || scoringPassphrase)) {
      void getSampleRequest(sampleRequestUrl);
    }
  }, [sampleRequestUrl, getSampleRequest, scoringPassphrase, defaultSecurityLevel]);

  React.useEffect(() => {
    if (sampleScoringConfig) {
      const sampleConfigWithoutRows = Object.keys(sampleScoringConfig).reduce((acc: { [key: string]: string }, key) => {
        acc[key] = '';
        return acc;
      }, {});
      setScoringConfig(sampleConfigWithoutRows);
    }
  }, [sampleScoringConfig]);

  return (
    <div className={classNames.form}>
      {(defaultSecurityLevel === 'passphrase_hashed' || defaultSecurityLevel === 'passphrase_hashed_deprecated') &&
      !isNew &&
      !isPassphraseSet ? (
        <>
          <TextField
            label="Passphrase"
            value={scoringPassphrase}
            onChange={(_ev, value) => setScoringPassphrase(value || '')}
            styles={{ root: { maxWidth: 460 } }}
            errorMessage={showPassphraseValidation ? 'Invalid passphrase' : undefined}
            onRenderLabel={(props?: ITextFieldProps) => (
              <div id={'load-hash'} className={classNames.infoLabel}>
                {props?.label}
                {validatingPassphrase ? <Loader styles={{ root: { marginLeft: 4 } }} size={0} /> : null}
              </div>
            )}
          />
          <Info styles={{ text: { alignContent: 'center' } }}>
            This deployment is hashed. Enter the passphrase to enable all quick scoring features.
          </Info>
          <div style={{ height: 15 }} />
        </>
      ) : null}
      {defaultSecurityLevel === 'passphrase_plain' || defaultSecurityLevel === 'no_security' || isPassphraseSet ? (
        <>
          <Label>Scoring configuration</Label>
          {/* TODO: validatingPassphrase is equivalent to isLoadingSampleConfig */}
          {validatingPassphrase ? (
            <div className={classNames.loaderWrapper}>
              <Loader label="Loading column names..." />
            </div>
          ) : (
            <KeyValuePairEditor
              config={scoringConfig}
              onUpdateConfig={(newConfig) => {
                setScoringConfig(newConfig);
                if (!isDefaultConfigChanged) setIsDefaultConfigChanged(true);
              }}
              styles={keyValuePairEditorStyles}
              keyLabel="Column name"
            />
          )}
          {!sampleScoringConfig && !validatingPassphrase ? (
            <Info
              styles={{
                text: {
                  alignContent: 'center',
                  color: theme.semanticColors?.inputErrorMessageText,
                  marginTop: 4,
                  marginBottom: 6,
                },
              }}
            >
              Failed to load column names for this deployment. Some features may be unavailable. Try refreshing the
              browser tab, and if the problem persists, contact our support team.
            </Info>
          ) : null}
          {!isSamplePopulated && !validatingPassphrase && sampleScoringConfig ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: 460 }}>
              <Button
                className={classNames.customEndpointButton}
                iconName="ReportAdd"
                text="Populate with sample data"
                disabled={!sampleScoringConfig}
                onClick={() => {
                  if (sampleScoringConfig) {
                    if (isDefaultConfigChanged) {
                      showDialog?.({
                        title: 'Populate with sample data?',
                        description: 'Your changes made to the scoring config will be replaced with sample data.',
                        onConfirm: () => {
                          setScoringConfig(sampleScoringConfig);
                          setIsSamplePopulated(true);
                        },
                        confirmationButtonText: 'Populate',
                      });
                    } else {
                      setScoringConfig(sampleScoringConfig);
                      setIsSamplePopulated(true);
                    }
                  }
                }}
                styles={buttonStylesStealth}
              />
            </div>
          ) : null}
          {isSamplePopulated ? <div style={{ height: 15 }} /> : null}
          <div id={'curl-scoring-request-label'} className={classNames.infoLabel}>
            cURL scoring request
            <Info isTooltip className={classNames.tooltip}>
              Auto-generated based on the scoring configuration.
            </Info>
          </div>
          <CodeBlock showCopyButton>
            {`curl -X POST -H "Content-Type: application/json" -d '${scoringConfigToJSON(scoringConfig)}' ${
              isSecured ? '-H "Authorization: Bearer ' : ''
            }${isSecured ? scoringPassphrase : ''}${isSecured ? '"' : ''} ${scorerEndpoint || '[SCORER_ENDPOINT]'}`}
          </CodeBlock>
          <div style={{ height: 25 }} />
          <div className={classNames.customEndpointButton} style={{ display: 'block' }}>
            <Button
              text="Score"
              onClick={score}
              styles={buttonStylesPrimary}
              loading={isScoring}
              loaderProps={{
                // TODO: Center the loading spinner.
                styles: loaderStylesSpinnerButtonPrimary,
              }}
            />
          </div>
        </>
      ) : null}
      {scoringResponse && (
        <>
          <h3>Scoring results</h3>
          <CodeBlock
            styles={{
              root: { borderRadius: 4 },
              pre: { borderRadius: 4 },
            }}
          >
            {scoringResponse}
          </CodeBlock>
        </>
      )}
    </div>
  );
};
